import { styled } from '@material-ui/core';
import { fileTypes, TEXT_VALIDATIONS } from './constant';
/**
 * this function is to convert markdown to html
 * @function mdToHtml
 ** @param {string} default parameter
 */
export function mdToHtml(str) {
  let tempStr = str;

  // Handle Markdown headers
  if (str.startsWith('-')) {
    tempStr = `<li>${str.substring(2)}</li>`;
  } else if (str.startsWith('####')) {
    tempStr = `<h4>${str.substring(5)}</h4>`;
  } else if (str.startsWith('###')) {
    tempStr = `<h3>${str.substring(4)}</h3>`;
  } else if (str.startsWith('##')) {
    tempStr = `<h2>${str.substring(3)}</h2>`;
  } else if (str.startsWith('#')) {
    tempStr = `<h1>${str.substring(2)}</h1>`;
  }

  // Convert valid Markdown links [text](url)
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  tempStr = tempStr.replace(linkRegex, (_, text, url) => `<a href="${url}">${text}</a>`);

  // Convert incomplete Markdown links [text] to plain text (removing slashes)
  const fallbackLinkRegex = /\[([^\]]+)\]/g;
  tempStr = tempStr.replace(fallbackLinkRegex, (_, text) => text.replace(/\\/g, ''));

  // Remove stray backslashes (e.g., '\word' becomes 'word')
  tempStr = tempStr.replace(/\\(?=\w)/g, '');

  // Handle bold (**text**) to <b>
  tempStr = tempStr.replace(/\*\*(.+?)\*\*/g, (_, text) => `<b>${text}</b>`);

  // Handle strong emphasis (__text__) to <strong>
  tempStr = tempStr.replace(/__(.+?)__/g, (_, text) => `<strong>${text}</strong>`);

  // Handle italic (_text_) to <i>
  tempStr = tempStr.replace(/_(.+?)_/g, (_, text) => `<i>${text}</i>`);

  // Remove unmatched bold (**), strong (__), or italics (_) markers
  tempStr = tempStr.replace(/\*\*|__|_/g, '');
  return tempStr;
}

/**
 * this function returns array of string after converting markdown into html
 * @function formatDescriptionDatal
 ** @param {string} default parameter
 */
export function formatDescriptionData(description) {
  const descriptionPoints = description?.split('\n').filter(Boolean); // split description string by new line and filtering non-empty strings
  return descriptionPoints?.map((e) => {
    return mdToHtml(e.trim());
  });
}

export const LabelName = styled('span')(({ theme }) => ({
  color: '#1D1E1F',
}));
export const Required = styled('span')(({ theme }) => ({
  color: '#FF0000',
}));

export const validateCustomFields = (fieldData) => {
  const {
    value,
    fieldType,
    regexpValidation,
    numberValidation,
    paragraphValidation,
    uploadFileValidation,
    fileType,
    fileSize,
  } = fieldData;
  // ? Validations for input type of text
  if (fieldType === 'TEXT') {
    // ? contains validation for text
    if (regexpValidation?.regexpType === TEXT_VALIDATIONS[0]) {
      if (!value.includes(regexpValidation?.pattern) && value !== '') {
        return {
          isValid: false,
          errorMessage: `Input does not include ${regexpValidation?.pattern}`,
        };
      }
    }
    // ? does not contain validation for text
    else if (regexpValidation?.regexpType === TEXT_VALIDATIONS[1]) {
      if (value.includes(regexpValidation?.pattern) && value !== '')
        return {
          isValid: false,
          errorMessage: `Input should not include ${regexpValidation?.pattern}`,
        };
    }
    // ? Matches validation for text
    else if (regexpValidation?.regexpType === TEXT_VALIDATIONS[2]) {
      if (value !== regexpValidation?.pattern && value !== '')
        return {
          isValid: false,
          errorMessage: `Input does not match the text: ${regexpValidation?.pattern}`,
        };
    }
    // ? Does not match validation for text
    else if (regexpValidation?.regexpType === TEXT_VALIDATIONS[3]) {
      if (value === regexpValidation?.pattern && value !== '')
        return {
          isValid: false,
          errorMessage: `Input should not match the text: ${regexpValidation?.pattern}`,
        };
    }
    // ? Email validation
    else if (regexpValidation?.regexpType === TEXT_VALIDATIONS[4]) {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(value) && value !== '')
        return {
          isValid: false,
          errorMessage: 'Email format is not valid',
        };
    }
    // ? URL validation
    else if (regexpValidation?.regexpType === TEXT_VALIDATIONS[5]) {
      const urlRegex = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(:\d{1,5})?(\/[^\s]*)?$/i;
      if (!urlRegex.test(value) && value !== '')
        return { isValid: false, errorMessage: 'URL is invalid' };
    }
  }
  // ? Validation for input type of Number
  else if (fieldType === 'NUMBER') {
    if (value < numberValidation?.min || value > numberValidation?.max)
      if (value !== '')
        return {
          isValid: false,
          errorMessage: `Number should be between ${numberValidation.min} and ${numberValidation.max}`,
        };
  }
  // ? Validation for input type of Paragraph
  else if (fieldType === 'PARAGRAPH') {
    // ? minimum characters validation
    if (paragraphValidation?.validationType === 'LENGTH') {
      if (
        paragraphValidation?.validationSubType === 'MINIMUM CHARACTERS' &&
        value?.length < paragraphValidation?.charCount
      )
        return {
          isValid: false,
          errorMessage: `Text should be atleast ${paragraphValidation?.charCount} characters long`,
        };
    }
    // ? maximum characters validation
    else if (
      paragraphValidation?.validationSubType === 'MAXIMUM CHARACTERS' &&
      value?.length > paragraphValidation?.charCount
    )
      return {
        isValid: false,
        errorMessage: `Text should not be greater than ${paragraphValidation?.charCount} characters`,
      };
  }
  // ? Validation for input type of Upload File
  else if (fieldType === 'UPLOAD FILE') {
    // ? if the selected file type is not allowed
    if (
      uploadFileValidation?.allowSpecificType &&
      !uploadFileValidation?.allowedFileTypes.includes(fileType)
    ) {
      const allowedFileTypes = uploadFileValidation?.allowedFileTypes.map((type) =>
        Object.keys(fileTypes).find((key) => fileTypes[key] === type),
      );
      return {
        isValid: false,
        errorMessage: `File type not allowed. Allowed file types are: ${allowedFileTypes?.map(
          (item) => item,
        )}`,
      };
    }

    // ? if file size is greater than allowed file size
    if (fileSize > uploadFileValidation?.maxFileSize)
      return {
        isValid: false,
        errorMessage: `File size should not be more than ${
          uploadFileValidation.maxFileSize / 1024 / 1024
        }MB`,
      };
  }
  return { isValid: true, errorMessage: '' };
};

export const isAnyRequiredCustomFieldEmpty = (customFormData, customFieldsValuesData) => {
  const requiredFields = customFormData?.filter((f) => f.required) ?? [];

  return requiredFields.some((field) => {
    const value = customFieldsValuesData?.find(
      (item) => item?.fieldName === field?.fieldName,
    )?.fieldValue;
    return (
      value === undefined ||
      value === null ||
      value === '' ||
      value === '<p><br></p>' || // ? this check is for the react-quill editor as it sometimes returns the text enclosed within paragraph tags
      value === '<br>' // ? this is also for react quill editor
    );
  });
};

export const debounce = (func, timeout = 300) => {
  let timer;
  return (/** @type {any} */ ...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, timeout);
  };
};
